import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const scatteredImages = [
  require("../../../assets/images/food1.png"),
  require("../../../assets/images/food2.png"),
  require("../../../assets/images/food3.png"),
  require("../../../assets/images/food4.png"),
  require("../../../assets/images/food5.png"),
  require("../../../assets/images/food6.png"),
  require("../../../assets/images/food7.png"),
];
const mainLogo = require("../../../assets/images/logoMain.png");

// Helper to get today’s date in YYYY-MM-DD
const getToday = () => new Date().toISOString().split("T")[0];

const calculateInitialPositions = (
  containerWidth,
  containerHeight,
  imageSize,
  padding = 20
) => {
  const positions = [];
  const usedPositions = [];

  const isOverlapping = (x, y) =>
    usedPositions.some((pos) => {
      const dx = pos.x - x;
      const dy = pos.y - y;
      return Math.sqrt(dx * dx + dy * dy) < imageSize + padding;
    });

  scatteredImages.forEach((_, index) => {
    let x, y;
    let attempts = 0;

    do {
      attempts++;
      if (index === 0) {
        x = padding;
        y = padding;
      } else if (index === 1) {
        x = containerWidth - imageSize - padding;
        y = padding;
      } else if (index === 2) {
        x = padding;
        y = containerHeight - imageSize - padding;
      } else if (index === 3) {
        x = containerWidth - imageSize - padding;
        y = containerHeight - imageSize - padding;
      } else if (index === 4) {
        x = imageSize - padding;
        y = containerHeight / 2 - imageSize / 3;
      } else if (index === 5) {
        x = containerWidth - imageSize * 2 - padding;
        y = containerHeight / 2 - imageSize / 3;
      } else {
        x =
          Math.random() * (containerWidth - imageSize - padding * 2) + padding;
        y =
          Math.random() * (containerHeight - imageSize - padding * 2) + padding;
      }
    } while (isOverlapping(x, y) && attempts < 50);

    positions.push({ x, y });
    usedPositions.push({ x, y });
  });

  return positions;
};

const MainLoading = () => {
  const [loaderState, setLoaderState] = useState({
    positions: [],
    stage: 0,
    showLoader: false,
    logoSize: 230,
    orbitImageSize: 150,
    orbitRadius: 250,
  });

  const {
    positions,
    stage,
    showLoader,
    logoSize,
    orbitImageSize,
    orbitRadius,
  } = loaderState;

  useEffect(() => {
    const lastShownDate = localStorage.getItem("loaderShownDate");
    const today = getToday();

    if (lastShownDate === today) {
      // Already shown today → skip
      setLoaderState((prev) => ({ ...prev, showLoader: false }));
      return;
    }

    // Show loader and save date
    localStorage.setItem("loaderShownDate", today);
    setLoaderState((prev) => ({ ...prev, showLoader: true }));

    const screenWidth = window.innerWidth;
    if (screenWidth <= 768) {
      setLoaderState((prev) => ({
        ...prev,
        logoSize: 100,
        orbitImageSize: 70,
        orbitRadius: 120,
      }));
    }

    const containerWidth = window.innerWidth;
    const containerHeight = window.innerHeight;

    setLoaderState((prev) => ({
      ...prev,
      positions: calculateInitialPositions(
        containerWidth,
        containerHeight,
        orbitImageSize
      ),
    }));

    const timers = [
      setTimeout(() => setLoaderState((prev) => ({ ...prev, stage: 1 })), 500),
      setTimeout(() => setLoaderState((prev) => ({ ...prev, stage: 2 })), 1200),
      setTimeout(() => setLoaderState((prev) => ({ ...prev, stage: 3 })), 3000),
      setTimeout(() => setLoaderState((prev) => ({ ...prev, stage: 4 })), 4500),
      setTimeout(
        () => setLoaderState((prev) => ({ ...prev, showLoader: false })),
        5000
      ),
    ];

    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  if (!showLoader) return null;

  return (
    <AnimatePresence>
      {showLoader && (
        <motion.div
          className="bg-gray w-full h-full fixed top-0 left-0 z-[999] flex items-center justify-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: stage === 4 ? 0.9 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          {scatteredImages.map((src, index) => {
            const pos = positions[index] || { x: 0, y: 0 };
            const angleOffset = (index / scatteredImages.length) * 2 * Math.PI;

            let animateProps;
            let transitionProps;

            if (stage === 0) {
              animateProps = { top: pos.y, left: pos.x, opacity: 1 };
              transitionProps = { duration: 0 };
            } else if (stage === 1) {
              const circleX =
                window.innerWidth / 2 +
                orbitRadius * Math.cos(angleOffset) -
                orbitImageSize / 2;
              const circleY =
                window.innerHeight / 2 +
                orbitRadius * Math.sin(angleOffset) -
                orbitImageSize / 2;

              animateProps = { top: circleY, left: circleX, opacity: 1 };
              transitionProps = { duration: 0.8, ease: "easeInOut" };
            } else if (stage === 2) {
              const steps = 50;
              const topPath = [];
              const leftPath = [];
              for (let i = 0; i <= steps; i++) {
                const angle = angleOffset + (i / steps) * 2 * Math.PI;
                topPath.push(
                  window.innerHeight / 2 +
                    orbitRadius * Math.sin(angle) -
                    orbitImageSize / 2
                );
                leftPath.push(
                  window.innerWidth / 2 +
                    orbitRadius * Math.cos(angle) -
                    orbitImageSize / 2
                );
              }
              animateProps = { top: topPath, left: leftPath, opacity: 1 };
              transitionProps = { duration: 5, ease: "linear" };
            } else {
              const logoX = window.innerWidth / 2 - orbitImageSize / 2;
              const logoY = window.innerHeight / 2 - orbitImageSize / 2;

              animateProps = { top: logoY, left: logoX, opacity: 0 };
              transitionProps = {
                duration: 0.6,
                delay: index * 0.15,
                ease: "easeInOut",
              };
            }

            return (
              <motion.img
                key={index}
                src={src}
                style={{
                  width: orbitImageSize,
                  height: orbitImageSize,
                  position: "absolute",
                  borderRadius: "10px",
                  zIndex: 1,
                }}
                animate={animateProps}
                transition={transitionProps}
              />
            );
          })}

          {/* Main Logo */}
          <div className="w-full max-w-[1758px] mx-auto fixed left-0 top-0 right-0 z-[800] px-2 sm:px-2 md:px-12 lg:px-14 xl:px-16">
            <div className="flex-1 flex justify-center mx-auto relative h-[53px]">
              <motion.img
                src={mainLogo}
                style={{
                  position: "absolute",
                  top: window.innerHeight / 2 - logoSize / 2,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: logoSize,
                  height: "auto",
                  zIndex: 10,
                }}
                animate={
                  stage === 2
                    ? {
                        width: logoSize * 1.2,
                        transform: "translateX(-50%)",
                        top: window.innerHeight / 2 - (logoSize * 1.3) / 2,
                      }
                    : stage === 3
                    ? {
                        width: logoSize * 1.3,
                        transform: "translateX(-50%)",
                        top: window.innerHeight / 2 - (logoSize * 1.3) / 2,
                      }
                    : stage === 4
                    ? window.innerWidth <= 1024
                      ? { top: "15px", left: "50%", width: "165px" }
                      : { top: "10px", left: "11.2%", width: "190px" }
                    : {}
                }
                transition={{
                  duration: stage === 2 ? 2 : 0.8,
                  ease: "easeInOut",
                }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MainLoading;
